import {
  CustomerSupportDeviceOrder,
  CustomerSupportPatientWithPrescriptionData,
  CustomerSupportPrescription,
  CustomerSupportTroubleshootingItem,
  ErrorType,
  getDeviceOrderProgram,
  getPatientFullName,
  getPrescriptionProgram,
} from "@/model/CustomerSupport"
import { deviceOrderStatusLabel } from "@/model/DeviceOrders"
import ViewLink from "@/pages/patient-list/ViewLink"
import { buildPath, ROUTES } from "@/routes"
import { ChronoUnit, Instant, LocalDate } from "@js-joda/core"
import { createColumnHelper } from "@tanstack/react-table"
import Badge from "./components/Badge"
import CSFlagCell from "./components/CSFlagCell"
import OrderDeviceButton from "./components/OrderDeviceButton"
import ReadyToOrderButton from "./components/ReadyToOrderButton"

const prescriptionColumnHelper =
  createColumnHelper<CustomerSupportPrescription>()
const patientWithPrescriptionDataColumnHelper =
  createColumnHelper<CustomerSupportPatientWithPrescriptionData>()
const deviceOrdersColumnHelper =
  createColumnHelper<CustomerSupportDeviceOrder>()
const troubleshootingColumnHelper =
  createColumnHelper<CustomerSupportTroubleshootingItem>()

export const csFlagsColumns = [
  patientWithPrescriptionDataColumnHelper.accessor("organizationName", {
    header: "Clinic",
    size: 150,
    minSize: 120,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue()}
      </div>
    ),
  }),
  patientWithPrescriptionDataColumnHelper.accessor("patient", {
    header: "Patient Name",
    size: 180,
    minSize: 140,
    cell: (info) => (
      <div className="truncate" title={getPatientFullName(info.getValue())}>
        {getPatientFullName(info.getValue()) || "-"}
      </div>
    ),
  }),
  patientWithPrescriptionDataColumnHelper.accessor("prescriptionTypes", {
    header: "Program",
    size: 120,
    minSize: 100,
    cell: (info) => (
      <div>
        {info
          .getValue()
          .map((t) => getPrescriptionProgram(t))
          .join(" | ") || "-"}
      </div>
    ),
  }),
  patientWithPrescriptionDataColumnHelper.accessor(
    "patient.medicalRecordNumber",
    {
      header: "MRN",
      size: 140,
      minSize: 100,
      cell: (info) => (
        <div className="truncate" title={info.getValue()}>
          {info.getValue() || "-"}
        </div>
      ),
    }
  ),
  patientWithPrescriptionDataColumnHelper.accessor("patient.phoneNumber", {
    header: "Patient phone",
    size: 160,
    minSize: 130,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  patientWithPrescriptionDataColumnHelper.accessor(
    "patient.customerSupportNotes.reason",
    {
      header: "CS flag notes",
      size: 180,
      minSize: 140,
      cell: (info) => (
        <div className="truncate" title={info.getValue()}>
          {info.getValue() || "-"}
        </div>
      ),
    }
  ),
  patientWithPrescriptionDataColumnHelper.accessor(
    "patient.customerSupportNotes.flagged",
    {
      header: "CS Flag",
      size: 100,
      minSize: 90,
      cell: (info) => <CSFlagCell patient={info.row.original.patient} />,
    }
  ),
  patientWithPrescriptionDataColumnHelper.accessor(
    "patient.customerSupportNotes.notes",
    {
      header: "CS notes",
      size: 400,
      minSize: 200,
      cell: (info) => (
        <div className="truncate" title={info.getValue()}>
          {info.getValue() || "-"}
        </div>
      ),
    }
  ),
  patientWithPrescriptionDataColumnHelper.display({
    id: "actions",
    header: "",
    enableResizing: false,
    cell: (info) => (
      <ViewLink
        to={buildPath(ROUTES.SUPPORT.PATIENT_DETAIL, {
          bloomlifeId: info.row.original.patient.bloomlifeId,
        })}
      />
    ),
  }),
]

export const enrollmentColumns = [
  prescriptionColumnHelper.accessor("organizationName", {
    header: "Clinic",
    size: 150,
    minSize: 120,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue()}
      </div>
    ),
  }),
  prescriptionColumnHelper.accessor("patient", {
    header: "Patient Name",
    size: 180,
    minSize: 140,
    cell: (info) => (
      <div className="truncate" title={getPatientFullName(info.getValue())}>
        {getPatientFullName(info.getValue()) || "-"}
      </div>
    ),
  }),
  prescriptionColumnHelper.accessor("type", {
    header: "Program",
    size: 120,
    minSize: 100,
    cell: (info) => <div>{getPrescriptionProgram(info.getValue()) || "-"}</div>,
  }),
  prescriptionColumnHelper.accessor("caregiver.caregiverDisplayName", {
    header: "Provider",
    size: 180,
    minSize: 140,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  prescriptionColumnHelper.accessor("patient.medicalRecordNumber", {
    header: "MRN",
    size: 140,
    minSize: 100,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  prescriptionColumnHelper.accessor("patient.phoneNumber", {
    header: "Patient phone",
    size: 160,
    minSize: 130,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  prescriptionColumnHelper.accessor("startDate", {
    header: "Days since enrollment",
    size: 200,
    minSize: 160,
    cell: (info) => {
      // TODO: Change this logic when we know how to get the days since enrollment
      const prescriptionDate = info.getValue()
      if (!prescriptionDate) return <div>-</div>

      const daysSince = ChronoUnit.DAYS.between(
        prescriptionDate,
        LocalDate.now()
      )
      return <div>{daysSince} days</div>
    },
  }),
  /*
  columnHelper.accessor("enrollmentStatus", {
    header: "Enrollment Status",
    size: 180,
    minSize: 150,
    cell: (info) => {
      const status = info.getValue()
      if (!status) return <div>-</div>
      // TODO: Make into seperate cell component
      const statusConfig: Record<
        string,
        { label: string; variant: "success" | "danger" | "warning" | "info" }
      > = {
        pending: { label: "Pending", variant: "warning" },
        "denied-sms": { label: "Denied SMS", variant: "danger" },
        "denied-device": { label: "Denied Device", variant: "danger" },
      }

      const config = statusConfig[status]
      if (!config) return <div>{status}</div>

      return <Badge label={config.label} variant={config.variant} />
    },
  }),*/
  prescriptionColumnHelper.accessor("patient.customerSupportNotes.flagged", {
    header: "CS Flag",
    size: 100,
    minSize: 90,
    cell: (info) => <CSFlagCell patient={info.row.original.patient} />,
  }),
  prescriptionColumnHelper.accessor("patient.customerSupportNotes.notes", {
    header: "CS notes",
    size: 400,
    minSize: 200,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  prescriptionColumnHelper.display({
    id: "actions",
    header: "",
    enableResizing: false,
    cell: (info) => (
      <div className="flex items-center gap-2">
        <ReadyToOrderButton prescription={info.row.original} />
        <ViewLink
          to={buildPath(ROUTES.SUPPORT.PATIENT_DETAIL, {
            bloomlifeId: info.row.original.patient.bloomlifeId,
          })}
        />
      </div>
    ),
  }),
]

export const orderDevicesColumns = [
  prescriptionColumnHelper.accessor("organizationName", {
    header: "Clinic",
    size: 150,
    minSize: 120,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue()}
      </div>
    ),
  }),
  prescriptionColumnHelper.accessor("patient", {
    header: "Patient Name",
    size: 180,
    minSize: 140,
    cell: (info) => (
      <div className="truncate" title={getPatientFullName(info.getValue())}>
        {getPatientFullName(info.getValue()) || "-"}
      </div>
    ),
  }),
  prescriptionColumnHelper.accessor("type", {
    header: "Program",
    size: 120,
    minSize: 100,
    cell: (info) => <div>{getPrescriptionProgram(info.getValue()) || "-"}</div>,
  }),
  prescriptionColumnHelper.accessor("patient", {
    header: "AT&T Compatibility",
    size: 180,
    minSize: 150,
    cell: (info) => {
      const isCompatible = info.getValue().isAttCompatible
      if (isCompatible === undefined) {
        return "N/A"
      }
      return (
        <Badge
          label={isCompatible ? "Compatible" : "Not Compatible"}
          icon={isCompatible ? "check" : "close"}
          variant={isCompatible ? "success" : "danger"}
        />
      )
    },
  }),
  prescriptionColumnHelper.display({
    id: "hasDevice",
    header: "Has Device",
    size: 130,
    minSize: 110,
    cell: (info) => {
      const prescription = info.row.original
      const hasDevice =
        (prescription.type === "diabetes" &&
          prescription.patient.hasBloodGlucoseMonitor) ||
        (prescription.type === "hypertension" &&
          prescription.patient.hasBloodPressureMonitor)

      return (
        <div className="flex items-center">
          {hasDevice ? (
            <span className="material-icons text-icon-lg text-green-600">
              check
            </span>
          ) : (
            <span className="material-icons text-icon-lg text-gray-400">
              close
            </span>
          )}
        </div>
      )
    },
  }),
  prescriptionColumnHelper.accessor("patient.customerSupportNotes.flagged", {
    header: "CS Flag",
    size: 100,
    minSize: 90,
    cell: (info) => <CSFlagCell patient={info.row.original.patient} />,
  }),
  prescriptionColumnHelper.accessor("patient.customerSupportNotes.notes", {
    header: "CS notes",
    size: 400,
    minSize: 200,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  prescriptionColumnHelper.display({
    id: "actions",
    header: "",
    enableResizing: false,
    cell: (info) => (
      <div className="flex items-center gap-2">
        <OrderDeviceButton
          bloomlifeId={info.row.original.patient.bloomlifeId}
          patientName={getPatientFullName(info.row.original.patient)}
          attCompatibility={info.row.original.patient.isAttCompatible}
          prescriptionId={info.row.original.id}
          prescriptionType={info.row.original.type}
        />
        <ViewLink
          to={buildPath(ROUTES.SUPPORT.PATIENT_DETAIL, {
            bloomlifeId: info.row.original.patient.bloomlifeId,
          })}
        />
      </div>
    ),
  }),
]

export const shippingColumns = [
  deviceOrdersColumnHelper.accessor("organizationName", {
    header: "Clinic",
    size: 150,
    minSize: 120,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue()}
      </div>
    ),
  }),
  deviceOrdersColumnHelper.accessor("patient", {
    header: "Patient Name",
    size: 180,
    minSize: 140,
    cell: (info) => (
      <div className="truncate" title={getPatientFullName(info.getValue())}>
        {getPatientFullName(info.getValue()) || "-"}
      </div>
    ),
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.type", {
    header: "Program",
    size: 120,
    minSize: 100,
    cell: (info) => <div>{getDeviceOrderProgram(info.getValue()) || "-"}</div>,
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.orderCreatedAt", {
    header: "Days since order placed",
    size: 200,
    minSize: 170,
    cell: (info) => (
      <div>
        {ChronoUnit.DAYS.between(info.getValue(), Instant.now()) || 0} days
      </div>
    ),
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.vendor", {
    header: "Vendor",
    size: 100,
    minSize: 70,
    cell: (info) => <div>{info.getValue()}</div>,
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.vendorOrderId", {
    header: "Order ID",
    size: 70,
    minSize: 70,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.orderNumber", {
    header: "Order Number",
    size: 150,
    minSize: 120,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.carrier", {
    header: "Shipping Carrier",
    size: 100,
    minSize: 80,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.trackingNumber", {
    header: "Tracking number",
    size: 160,
    minSize: 130,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.status", {
    header: "Tracking status",
    size: 160,
    minSize: 140,
    cell: (info) => {
      const status = info.getValue()
      return <div>{status ? deviceOrderStatusLabel(status) : "-"}</div>
    },
  }),
  deviceOrdersColumnHelper.accessor("deviceOrder.orderDeliveredAt", {
    header: "Days since delivered",
    size: 200,
    minSize: 170,
    cell: (info) => {
      const deliveredAt = info.getValue()

      if (!deliveredAt) {
        return <div>-</div>
      } else {
        return (
          <div>
            {ChronoUnit.DAYS.between(deliveredAt, Instant.now()) || 0} days
          </div>
        )
      }
    },
  }),
  deviceOrdersColumnHelper.accessor("patient.customerSupportNotes.flagged", {
    header: "CS Flag",
    size: 100,
    minSize: 90,
    cell: (info) => <CSFlagCell patient={info.row.original.patient} />,
  }),
  deviceOrdersColumnHelper.accessor("patient.customerSupportNotes.notes", {
    header: "CS notes",
    size: 400,
    minSize: 200,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  deviceOrdersColumnHelper.display({
    id: "actions",
    header: "",
    enableResizing: false,
    cell: (info) => (
      <ViewLink
        to={buildPath(ROUTES.SUPPORT.PATIENT_DETAIL, {
          bloomlifeId: info.row.original.patient.bloomlifeId,
        })}
      />
    ),
  }),
]

export const systemTroubleshootingColumns = [
  troubleshootingColumnHelper.accessor("organizationName", {
    header: "Clinic",
    size: 150,
    minSize: 120,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue()}
      </div>
    ),
  }),
  troubleshootingColumnHelper.accessor("patient", {
    header: "Patient Name",
    size: 180,
    minSize: 140,
    cell: (info) => (
      <div className="truncate" title={getPatientFullName(info.getValue())}>
        {getPatientFullName(info.getValue()) || "-"}
      </div>
    ),
  }),
  troubleshootingColumnHelper.accessor("prescriptionType", {
    header: "Program",
    size: 120,
    minSize: 100,
    cell: (info) => <div>{getPrescriptionProgram(info.getValue()) || "-"}</div>,
  }),
  troubleshootingColumnHelper.accessor("patient.medicalRecordNumber", {
    header: "MRN",
    size: 140,
    minSize: 100,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  troubleshootingColumnHelper.accessor("error", {
    header: "Error Type",
    size: 200,
    minSize: 160,
    cell: (info) => {
      // TODO: Make into seperate cell component
      const errorType = info.getValue()
      if (!errorType) return <div>-</div>

      const errorLabels: Record<ErrorType, string> = {
        "device-not-suspended": "Device Not Suspended",
        "duplicate-active-devices": "2 Active Devices of Same Type",
      }

      return (
        <Badge label={errorLabels[errorType] || errorType} variant="danger" />
      )
    },
  }),
  /*troubleshootingColumnHelper.accessor("detectionDate", {
    header: "Detection Date",
    size: 150,
    minSize: 120,
    cell: (info) => {
      const dateStr = info.getValue()
      if (!dateStr) return <div>-</div>
      const date = LocalDate.parse(dateStr)
      return <div>{formatDate(date, "MM/dd/yyyy")}</div>
    },
  }),*/
  troubleshootingColumnHelper.accessor("patient.customerSupportNotes.flagged", {
    header: "CS Flag",
    size: 100,
    minSize: 90,
    cell: (info) => <CSFlagCell patient={info.row.original.patient} />,
  }),
  troubleshootingColumnHelper.accessor("patient.customerSupportNotes.notes", {
    header: "CS notes",
    size: 400,
    minSize: 200,
    cell: (info) => (
      <div className="truncate" title={info.getValue()}>
        {info.getValue() || "-"}
      </div>
    ),
  }),
  troubleshootingColumnHelper.display({
    id: "actions",
    header: "",
    enableResizing: false,
    cell: (info) => (
      <ViewLink
        to={buildPath(ROUTES.SUPPORT.PATIENT_DETAIL, {
          bloomlifeId: info.row.original.patient.bloomlifeId,
        })}
      />
    ),
  }),
]
